/* eslint-disable @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import { MouseEventHandler } from 'react';
import { NavLink } from 'react-router-dom';
import { Link as MuiLink, Stack, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { Tag } from '../shared/Tag';
import { Box } from '@mui/material';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { SGTooltip } from './SGTooltip';
import LockIcon from '@mui/icons-material/Lock';
import useIsMobile from 'hooks/useIsMobile';
import { SubLevel } from 'types';
import { SubLevelTitles } from 'util/shared';

type LinkProps = {
  to: string;
  isLabelShown: boolean;
  icon?: JSX.Element;
  text?: string;
  onClick?: MouseEventHandler<any> | undefined;
  tagText?: string | null;
  relativeLink?: boolean;
  isLocked?: boolean;
  isSpecialUnlocked?: boolean;
  styleOverrides?: React.CSSProperties;
  subLevelRequired?: SubLevel;
  disableActive?: boolean;
};

export const Link = ({
  children,
  to,
  isLabelShown,
  icon,
  text,
  onClick,
  tagText,
  relativeLink = true,
  isLocked,
  isSpecialUnlocked,
  subLevelRequired,
  styleOverrides,
  disableActive,
}: React.PropsWithChildren<LinkProps>) => {
  const theme = useTheme();
  const isMobile = useIsMobile();

  const styling = (isActive: boolean, disableActive?: boolean) => ({
    color:
      isActive && !disableActive
        ? theme.palette.sgGreen
        : theme.palette.text.secondary,
    textDecoration: 'none',
    fontSize: '14px',
    alignItems: 'center',
    fontWeight: 500,
    ...styleOverrides,
  });

  const allChildren = (
    <Stack direction="row" sx={{ gap: '6px', alignItems: 'center' }}>
      {!isLabelShown && icon ? (
        <SGTooltip title={text} placement="right">
          {icon}
        </SGTooltip>
      ) : (
        icon
      )}
      {isLabelShown && (
        <>
          {text && <span className="hoverable">{text}</span>}
          {isSpecialUnlocked ? (
            <LockOpenIcon
              fontSize="small"
              sx={{ color: theme.palette.sgGreen }}
            />
          ) : (
            isLocked &&
            subLevelRequired != null && (
              <Stack
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  padding: 1,
                  borderRadius: '4px',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: 9,
                    color: theme.palette.getContrastText(
                      theme.palette.primary.main,
                    ),
                    fontWeight: 'bold',
                  }}
                >
                  {SubLevelTitles[subLevelRequired]}
                </Typography>
                <LockIcon
                  sx={{
                    fontSize: 9,
                    color: theme.palette.getContrastText(
                      theme.palette.primary.main,
                    ),
                  }}
                />
              </Stack>
            )
          )}
          {tagText != null && <Tag label={tagText} color="#1AA251" />}
          {children}
        </>
      )}
    </Stack>
  );

  return (
    <Box
      sx={{
        width: '100%',
        borderRadius: 1,
        padding: '4px',
        '&:hover': {
          backgroundColor: alpha(theme.palette.sgGreen, 0.35),
          transform: 'scale(1.02)',
          transition: theme.transitions.create('transform', {
            duration: theme.transitions.duration.short,
          }),
        },
      }}
    >
      {relativeLink ? (
        <NavLink
          to={to}
          onClick={onClick}
          style={({ isActive }) => styling(isActive, disableActive) as any}
          className="navigation-link"
        >
          {allChildren}
        </NavLink>
      ) : (
        <MuiLink
          href={to}
          target={!isMobile ? '_blank' : ''}
          style={styling(false, disableActive) as any}
          onClick={onClick}
        >
          {allChildren}
        </MuiLink>
      )}
    </Box>
  );
};
